import jsPDF from "jspdf";

/**
 * A branded PDF of the same rows a CSV would carry — the clinic's name on a
 * titled, period-stamped table. Drawn by hand (jsPDF is the only PDF library
 * the app ships), landscape so a ten-column ledger stays legible.
 */
export function exportPdf(
  filename: string,
  clinic: string,
  title: string,
  headers: string[],
  rows: unknown[][],
  color = "#4f46e5",
): void {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  const colW = (W - M * 2) / headers.length;
  const rgb = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
  const clip = (t: string, w: number) => {
    let s = t;
    while (s.length > 1 && doc.getTextWidth(s) > w - 8) s = s.slice(0, -2);
    return s === t ? s : `${s}…`;
  };

  let y = 0;
  let page = 0;
  const head = () => {
    page += 1;
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.rect(0, 0, W, 6, "F");
    doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(20);
    doc.text(clinic, M, 36);
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(110);
    doc.text(title, M, 52);
    doc.text(`Page ${page}`, W - M, 52, { align: "right" });
    y = 74;
    doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(110);
    headers.forEach((h, i) => doc.text(h.toUpperCase(), M + i * colW + 4, y));
    doc.setDrawColor(200).line(M, y + 5, W - M, y + 5);
    y += 20;
  };
  head();
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(30);
  for (const r of rows) {
    if (y > H - M) {
      doc.addPage();
      head();
      doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(30);
    }
    r.forEach((c, i) =>
      doc.text(clip(String(c ?? ""), colW), M + i * colW + 4, y),
    );
    y += 16;
  }
  doc.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
}

export interface StatementPdfPerson {
  name: string;
  deal: string;
  columns: string[];
  rows: unknown[][];
  totals: Array<[string, string]>;
}

/** One statement per person, each on its own page, in a single branded file. */
export function exportStatementsPdf(
  filename: string,
  clinic: string,
  title: string,
  people: StatementPdfPerson[],
  color = "#4f46e5",
): void {
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  const rgb = [1, 3, 5].map((i) => parseInt(color.slice(i, i + 2), 16));
  people.forEach((p, n) => {
    if (n > 0) doc.addPage();
    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
    doc.rect(0, 0, W, 6, "F");
    doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(20);
    doc.text(clinic, M, 36);
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(110);
    doc.text(title, M, 52);
    doc.setFont("helvetica", "bold").setFontSize(16).setTextColor(20);
    doc.text(p.name, M, 86);
    doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(110);
    doc.text(p.deal, M, 102);
    const colW = (W - M * 2) / p.columns.length;
    let y = 130;
    doc.setFont("helvetica", "bold").setFontSize(8).setTextColor(110);
    p.columns.forEach((h, i) => doc.text(h.toUpperCase(), M + i * colW + 4, y));
    doc.setDrawColor(200).line(M, y + 5, W - M, y + 5);
    y += 20;
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(30);
    for (const r of p.rows) {
      if (y > H - M - 60) break;
      r.forEach((c, i) =>
        doc.text(
          String(c ?? "").slice(0, Math.floor(colW / 5)),
          M + i * colW + 4,
          y,
        ),
      );
      y += 16;
    }
    y += 12;
    p.totals.forEach(([k, v]) => {
      doc.setFont("helvetica", "normal").setTextColor(110).text(k, M, y);
      doc
        .setFont("helvetica", "bold")
        .setTextColor(20)
        .text(v, W - M, y, { align: "right" });
      y += 16;
    });
  });
  doc.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
}
